/**
 * scenePollAdapter - Normalizes the scene-generation (T2V / I2V) job status
 * into the generic `PollUpdate` consumed by GenerationContext.
 *
 * Scene generation reads the unified job route (`GET /api/v1/jobs/{job_id}`),
 * so this adapter speaks the canonical `UnifiedJob` shape: `progress` is
 * already 0-100 and the clip lands on `result_url`.
 *
 * Self-registers on module load so GenerationContext can resume polling a
 * persisted scene job after a tab switch or a page reload — the capability
 * SceneTab lacked entirely until 2026-08-19, when a finished clip was
 * unreachable because the only reference to its job id lived in a component
 * closure that unmounted.
 */

import { getJobStatus, isJobTerminal, type UnifiedJob } from '../jobs';
import type { PollAdapter, PollUpdate } from '../../types/generationJob';
import { sanitizeBackendError } from '../../utils/errorSanitizer';
import { registerPollAdapter } from './pollAdapterRegistry';

export interface SceneJobResult {
  /** URL of the generated clip. */
  resultUrl: string;
  /** USD cost reported by the backend, when present. */
  cost?: number;
}

export const scenePollAdapter: PollAdapter = async (
  jobId: string
): Promise<PollUpdate<SceneJobResult>> => {
  let data: UnifiedJob;
  try {
    data = await getJobStatus<UnifiedJob>(jobId);
  } catch (err) {
    // A job the backend has never heard of is a stale id from a previous
    // session (in-memory stores do not survive a backend restart). Report it
    // as expired so GenerationContext drops it quietly instead of showing a
    // failure banner for something the user cannot act on.
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) {
      return {
        done: true,
        status: 'expired',
        error: { message: 'Stale scene job from a previous session', code: 'JOB_NOT_FOUND' },
      };
    }
    return {
      done: true,
      status: 'failed',
      error: {
        message: err instanceof Error ? err.message : 'Failed to get scene generation status',
        sanitized: sanitizeBackendError(null),
      },
    };
  }

  if (!isJobTerminal(data.status)) {
    return {
      done: false,
      status: 'running',
      progress: typeof data.progress === 'number' ? data.progress : undefined,
      stage: data.current_stage ?? undefined,
      message: data.message,
    };
  }

  if (data.status === 'completed' && data.result_url) {
    const cost = (data.extra_metadata?.cost as number | undefined) ?? undefined;
    return {
      done: true,
      status: 'completed',
      progress: 100,
      result: { resultUrl: data.result_url, cost },
    };
  }

  if (data.status === 'cancelled') {
    return {
      done: true,
      status: 'cancelled',
      error: { message: data.error ?? 'Scene generation was cancelled' },
    };
  }

  // Completed WITHOUT a result_url is a failure, not a success. Reporting it as
  // completed would render an empty player and tell the user their paid clip
  // was fine.
  return {
    done: true,
    status: 'failed',
    error: {
      message:
        data.error ??
        (data.status === 'completed'
          ? 'Scene generation finished without producing a clip'
          : 'Scene generation failed'),
      sanitized: sanitizeBackendError(data),
    },
  };
};

registerPollAdapter('scene', scenePollAdapter);
// Image-to-Video polls the same unified job route; only the results
// bucket differs, so the adapter is shared and registered under both.
registerPollAdapter('imageToVideo', scenePollAdapter);
